import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import {
  ClientAccessQueryKey,
  UsersQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import { ClientAccessUser, User } from "@/types/User.tsx";
import { Loader } from "@/components";
import ClientAccessCard from "@/components/Group/ClientAccessCard.tsx";
import { UpdateGroup } from "@/types/Groups.tsx";
import { ValidationErrors } from "@/types/ValidationError.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import { useSnackBarAlert } from "@/hooks/useSnackbar";
import { isBitramp } from "@/utils/utils";

const ClientAccess: React.FC = () => {
  const queryClient = useQueryClient();
  const [emailEmploye, setEmailEmploye] = React.useState<string>("");
  const [addEmailEmploye, setAddEmailEmploye] = React.useState<boolean>(false);

  const { showSnackBar } = useSnackBarAlert();

  const { data: users, isLoading: isLoadingUsers } = useQuery<
    ApiResponse<User[]>
  >({
    queryFn: async () => GenericApi.get<User[]>(ApiRoutes.USERS),
    queryKey: [UsersQueryKey],
  });

  const {
    data: clients,
    isLoading,
    error,
  } = useQuery<ApiResponse<ClientAccessUser[]>>({
    queryFn: async () =>
      GenericApi.get<ClientAccessUser[]>(ApiRoutes.STAFF_ACCESS),
    queryKey: [ClientAccessQueryKey],
  });

  const editClientAccess = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    { id: number; userIds: number[] }
  >({
    mutationFn: (data) =>
      GenericApi.put<UpdateGroup>(`${ApiRoutes.CLIENT_ACCESS}/${data.id}`, {
        users: data.userIds,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      await queryClient.invalidateQueries({ queryKey: [ClientAccessQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const addEmailEmployeMutate = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    { email: string }
  >({
    mutationFn: (data) => {
      return GenericApi.put<{ email: string }>(`${ApiRoutes.STAFF_PROMOTE}`, {
        email: data.email,
      });
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      showSnackBar(
        "Staff added",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      queryClient.invalidateQueries({ queryKey: [ClientAccessQueryKey] });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const handleAddEmailEmploye = (e: React.FormEvent) => {
    e.preventDefault(); // Previne comportamentul implicit al formularului
    addEmailEmployeMutate.mutate({
      email: emailEmploye,
    });
  };

  // const promoteToStaffMutation = useMutation<
  //     ApiResponse<unknown>,
  //     ApiError<ValidationErrors>,
  //     { email: string }
  // >({
  //     mutationFn: (data) =>
  //         GenericApi.put<UpdateGroup>(`${ApiRoutes.STAFF_PROMOTE}`, data),
  //     onSuccess: async ({data}) => {
  //         CustomLogger.log(data);
  //         await queryClient.invalidateQueries({queryKey: [ClientAccessQueryKey]});
  //     },
  //     onError: async ({error}) => {
  //         CustomLogger.error(error);
  //     },
  // });

  const handleAddUserToAccessList = (clientId: number, userIds: number[]) => {
    editClientAccess.mutate({
      id: clientId,
      userIds: userIds,
    });
  };

  if (isLoading || isLoadingUsers) {
    return <Loader />;
  }

  if (error || clients?.error || users?.error) {
    return (
      <div>
        Error:{" "}
        {error?.message || clients?.error?.message || users?.error?.message}
      </div>
    );
  }

  return (
    <div className="flex h-full justify-start flex-col items-start w-full p-[2rem] gap-4 Container pshadow">
      <div className="w-full flex flex-col justify-end items-end">
        <div
          className={`border rounded-[0.625rem] text-[1rem] font-[700] font-satoshi ${
            isBitramp()
              ? "border-[#8AFEFD] text-[#8AFEFD]"
              : "border-[#202A43] text-[#000]"
          }`}
        >
          <div
            onClick={() => setAddEmailEmploye(true)}
            className="px-[0.9rem] py-[0.4rem] cursor-pointer flex items-center gap-2"
          >
            Add employee <span className="text-[1.4375rem]">+</span>
          </div>
          {addEmailEmploye && (
            <div
              className={`w-full h-[1px]  ${
                isBitramp() ? "bg-[#8AFEFD]" : "bg-[#D5D5D5]"
              }`}
            ></div>
          )}
          {addEmailEmploye && (
            <div>
              <form className="flex flex-col">
                <input
                  type="email"
                  placeholder="Add emplyee E-mail"
                  className={`border border-[#D5D5D5] rounded-[0.5rem] px-4 py-2 mx-4 my-2 text-[#000] text-[1rem] font-[300] font-satoshi w-[20rem] Input  placeholder:font-[300] ${
                    isBitramp()
                      ? "placeholder:text-[#464646]"
                      : "placeholder:text-[#e4e5ea]"
                  }`}
                  value={emailEmploye}
                  onChange={(e) => setEmailEmploye(e.target.value)}
                />
                <div className="flex justify-end items-center mt-2 px-4 py-3">
                  <button
                    onClick={() => setAddEmailEmploye(false)}
                    className={`mr-2 ${
                      isBitramp() ? "Edit_button_bitramp" : "Outline_red_button"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => handleAddEmailEmploye(e)}
                    className={` ${isBitramp() ? "Upload_button_bitramp" : "Primary_button"}`}
                  >
                    Add
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      {clients?.data?.map((client, index) => (
        <ClientAccessCard
          key={client.id}
          client={client}
          users={users?.data ?? null}
          isFirst={index === 0}
          handleAddUserToAccessList={handleAddUserToAccessList}
        />
      ))}
    </div>
  );
};

export default ClientAccess;
